import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Plus, Trash2 } from 'lucide-react';
import type { ActualizarBorradorRequest, EmitirGastoComunRequest } from '@condo/contracts';
import { ActualizarBorradorRequestSchema } from '@condo/contracts';
import { useCondoContext } from '@/features/auth/use-condo-context';
import { useGastoComun, useActualizarBorrador } from '@/features/gastos-comunes/api';
import { EstadoBadge } from '@/features/gastos-comunes/estado-badge';
import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ClpInput } from '@/components/common/clp-input';
import { formatClp } from '@/lib/format/currency';

const CATEGORIAS = [
  { value: 'administracion', label: 'Administracion' },
  { value: 'mantencion', label: 'Mantencion' },
  { value: 'aseo', label: 'Aseo' },
  { value: 'seguridad', label: 'Seguridad' },
  { value: 'servicios_basicos', label: 'Servicios basicos' },
  { value: 'seguros', label: 'Seguros' },
  { value: 'fondo_reserva', label: 'Fondo de reserva' },
  { value: 'extraordinario', label: 'Extraordinario' },
  { value: 'otros', label: 'Otros' },
] as const;

export function EditarBorradorRoute() {
  const { gastoId } = useParams();
  const { condoId } = useCondoContext();
  const navigate = useNavigate();
  const { data, isLoading } = useGastoComun(condoId, gastoId);

  if (isLoading || !data) {
    return (
      <PageContainer>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-6 h-96" />
      </PageContainer>
    );
  }

  const { gastoComun } = data;

  if (gastoComun.estado !== 'borrador') {
    return (
      <PageContainer>
        <Button variant="ghost" asChild size="sm" className="mb-4">
          <Link to={`/app/condominios/${condoId}/gastos-comunes/${gastoId}`}>
            <ArrowLeft className="h-4 w-4" />
            Volver al detalle
          </Link>
        </Button>
        <PageHeader
          eyebrow="Error"
          title="No se puede editar"
          subtitle="Solo los gastos comunes en estado borrador pueden ser editados."
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Button variant="ghost" asChild size="sm" className="mb-4">
        <Link to={`/app/condominios/${condoId}/gastos-comunes/${gastoId}`}>
          <ArrowLeft className="h-4 w-4" />
          Volver al detalle
        </Link>
      </Button>
      <PageHeader
        eyebrow="Editar borrador"
        title="Editar gasto comun"
        subtitle="Modifica las lineas o la fecha de vencimiento del borrador."
        meta={
          <div className="mt-2">
            <EstadoBadge estado="borrador" />
          </div>
        }
        ruleSpacing="mb-8"
      />

      <EditarBorradorForm
        condoId={condoId}
        gastoId={gastoId!}
        gastoComun={gastoComun}
        onUpdated={() => navigate(`/app/condominios/${condoId}/gastos-comunes/${gastoId}`)}
      />
    </PageContainer>
  );
}

function EditarBorradorForm({
  condoId,
  gastoId,
  gastoComun,
  onUpdated,
}: {
  condoId: string;
  gastoId: string;
  gastoComun: { lineas: EmitirGastoComunRequest['lineas']; fechaVencimiento: string };
  onUpdated: () => void;
}) {
  const mutation = useActualizarBorrador(condoId);

  const form = useForm<ActualizarBorradorRequest>({
    resolver: zodResolver(ActualizarBorradorRequestSchema),
    defaultValues: {
      lineas: gastoComun.lineas,
      fechaVencimiento: gastoComun.fechaVencimiento,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'lineas',
  });

  const lineas = form.watch('lineas') ?? [];
  const montoTotal = lineas.reduce((s, l) => s + (Number(l.monto) || 0), 0);

  async function onSubmit(values: ActualizarBorradorRequest) {
    try {
      await mutation.mutateAsync({ gastoId, ...values });
      toast.success('Borrador actualizado');
      onUpdated();
    } catch {
      toast.error('No se pudo actualizar el borrador');
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="max-w-2xl space-y-6">
      <div>
        <Label htmlFor="vto">Fecha de vencimiento</Label>
        <Input
          id="vto"
          type="date"
          className="mt-1.5 max-w-xs tabular"
          {...form.register('fechaVencimiento')}
        />
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label>Lineas del gasto comun</Label>
          <span className="text-[0.6875rem] text-[var(--color-ink-faint)]">
            {lineas.length} linea(s)
          </span>
        </div>
        <ul className="mt-2 space-y-2">
          {fields.map((field, index) => (
            <li
              key={field.id}
              className="rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper-dim)] p-3"
            >
              <div className="grid gap-2 md:grid-cols-[170px_1fr_150px_32px]">
                <Select
                  value={form.watch(`lineas.${index}.categoria`)}
                  onValueChange={(v) =>
                    form.setValue(
                      `lineas.${index}.categoria`,
                      v as EmitirGastoComunRequest['lineas'][number]['categoria'],
                      { shouldValidate: true },
                    )
                  }
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIAS.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Concepto"
                  className="h-9"
                  {...form.register(`lineas.${index}.concepto`)}
                />
                <ClpInput
                  value={form.watch(`lineas.${index}.monto`)}
                  onChange={(v) =>
                    form.setValue(`lineas.${index}.monto`, v, {
                      shouldValidate: true,
                    })
                  }
                  className="h-9"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => remove(index)}
                  aria-label="Quitar linea"
                  disabled={fields.length === 1}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-2"
          onClick={() =>
            append({ categoria: 'otros', concepto: '', monto: 0 })
          }
        >
          <Plus className="h-4 w-4" />
          Agregar linea
        </Button>
      </div>

      <div className="flex items-center justify-between border-t border-[var(--color-hairline)] pt-4">
        <div>
          <p className="eyebrow">Monto total</p>
          <p className="font-display-tight mt-1 text-3xl font-semibold tabular text-[var(--color-primary)]">
            {formatClp(montoTotal)}
          </p>
        </div>
        <Button type="submit" size="lg" disabled={mutation.isPending}>
          {mutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Guardando...
            </>
          ) : (
            'Guardar cambios'
          )}
        </Button>
      </div>
    </form>
  );
}
